import type { NormalizedLandmarkList } from "@mediapipe/pose";

export type RowingMode = "sculling" | "sweep";
export type CameraView = "side" | "diagonal" | "launch" | "bow";
export type Phase = "Catch" | "Drive" | "Finish" | "Recovery";
export type ConfidenceLabel = "High" | "Medium" | "Low";

export type JointConfidence = {
  knee: number;
  kneeRise: number;
  elbow: number;
  trunk: number;
  wrist: number;
};

export type RawPoseSample = {
  time: number;
  landmarks: NormalizedLandmarkList;
  kneeAngle: number;
  hipAngle: number;
  elbowAngle: number;
  trunkAngle: number;
  kneeRiseRelative: number;
  wristRelative: number;
  hipRelative: number;
  shoulderRelative: number;
  visibility: number;
  jointConfidence: JointConfidence;
  side: "left" | "right";
};

export type PoseSample = RawPoseSample & {
  phase: Phase;
  strokeId: number | null;
};

export type StrokeCycle = {
  id: number;
  catchIndex: number;
  legEndIndex: number;
  finishIndex: number;
  nextCatchIndex: number;
  catchTime: number;
  legEndTime: number;
  finishTime: number;
  nextCatchTime: number;
  driveDuration: number;
  recoveryDuration: number;
  confidence: number;
  findings: string[];
};

export type PhaseScore = {
  phase: Phase;
  score: number | null;
  confidence: number;
  label: "Strong" | "On track" | "Watch" | "Focus" | "Unavailable";
  summary: string;
};

export type TechniqueFinding = {
  id: string;
  title: string;
  description: string;
  evidence: string;
  cue: string;
  drill: string;
  frequency: number;
  confidence: number;
  seekTime: number;
  tone: "focus" | "watch";
};

export type PracticeItem = {
  title: string;
  cue: string;
  drill: string;
  check: string;
  seekTime: number;
};

export type AnalysisMetrics = {
  strokeRate: number | null;
  driveRecovery: number | null;
  sequence: number | null;
  consistency: number | null;
  confidence: number;
  confidenceLabel: ConfidenceLabel;
  catchKnee: number | null;
  finishKnee: number | null;
  trunkSwing: number | null;
  elbowRange: number | null;
  analyzedSeconds: number;
  strokesObserved: number;
  periodicity: number;
  overallScore: number | null;
  validFramePercent: number;
};

export type AnalysisResult = {
  samples: PoseSample[];
  cycles: StrokeCycle[];
  metrics: AnalysisMetrics;
  phaseScores: PhaseScore[];
  findings: TechniqueFinding[];
  practicePlan: PracticeItem[];
  limitations: string[];
};

export type LandmarkMeasurement = Omit<RawPoseSample, "time" | "landmarks">;

type CycleFeatures = {
  cycle: StrokeCycle;
  catchKnee: number;
  finishKnee: number;
  catchKneeRise: number;
  finishKneeRise: number;
  kneeExcursion: number;
  catchElbow: number;
  finishElbow: number;
  earlyArmFlex: number;
  earlyTrunkChange: number;
  earlyHandleFraction: number;
  earlyKneeBreak: number;
  recoveryTrackingConfidence: number;
  finalCompressionFraction: number;
  trunkSwing: number;
  driveRecovery: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const mean = (values: number[]) =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

const median = (values: number[]) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

const standardDeviation = (values: number[]) => {
  if (values.length < 2) return 0;
  const center = mean(values);
  return Math.sqrt(mean(values.map((value) => (value - center) ** 2)));
};

const percentile = (values: number[], p: number) => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = clamp(Math.round((sorted.length - 1) * p), 0, sorted.length - 1);
  return sorted[index];
};

const distance = (
  first: NormalizedLandmarkList[number],
  second: NormalizedLandmarkList[number],
) => Math.hypot(first.x - second.x, first.y - second.y);

const angleAt = (
  first: NormalizedLandmarkList[number],
  center: NormalizedLandmarkList[number],
  third: NormalizedLandmarkList[number],
) => {
  const firstAngle = Math.atan2(first.y - center.y, first.x - center.x);
  const secondAngle = Math.atan2(third.y - center.y, third.x - center.x);
  let degrees = Math.abs(((secondAngle - firstAngle) * 180) / Math.PI);
  if (degrees > 180) degrees = 360 - degrees;
  return degrees;
};

const torsoFromVertical = (
  shoulder: NormalizedLandmarkList[number],
  hip: NormalizedLandmarkList[number],
) => (Math.atan2(shoulder.x - hip.x, hip.y - shoulder.y) * 180) / Math.PI;

function chooseSide(landmarks: NormalizedLandmarkList) {
  const left = [11, 13, 15, 23, 25, 27];
  const right = [12, 14, 16, 24, 26, 28];
  const score = (indices: number[]) =>
    mean(indices.map((index) => landmarks[index]?.visibility ?? 0));
  return score(left) >= score(right)
    ? { side: "left" as const, indices: left }
    : { side: "right" as const, indices: right };
}

export function landmarkMeasurements(
  landmarks: NormalizedLandmarkList,
): LandmarkMeasurement | null {
  const selected = chooseSide(landmarks);
  const [shoulderIndex, elbowIndex, wristIndex, hipIndex, kneeIndex, ankleIndex] =
    selected.indices;
  const shoulder = landmarks[shoulderIndex];
  const elbow = landmarks[elbowIndex];
  const wrist = landmarks[wristIndex];
  const hip = landmarks[hipIndex];
  const knee = landmarks[kneeIndex];
  const ankle = landmarks[ankleIndex];
  if (![shoulder, elbow, wrist, hip, knee, ankle].every(Boolean)) return null;

  const torsoLength = Math.max(0.035, distance(shoulder, hip));
  const visibilityAt = (index: number) => landmarks[index]?.visibility ?? 0;
  const jointConfidence = {
    knee: mean([visibilityAt(hipIndex), visibilityAt(kneeIndex), visibilityAt(ankleIndex)]),
    kneeRise: mean([visibilityAt(shoulderIndex), visibilityAt(hipIndex), visibilityAt(kneeIndex)]),
    elbow: mean([visibilityAt(shoulderIndex), visibilityAt(elbowIndex), visibilityAt(wristIndex)]),
    trunk: mean([visibilityAt(shoulderIndex), visibilityAt(hipIndex)]),
    wrist: mean([visibilityAt(wristIndex), visibilityAt(ankleIndex)]),
  };

  return {
    side: selected.side,
    kneeAngle: angleAt(hip, knee, ankle),
    hipAngle: angleAt(shoulder, hip, knee),
    elbowAngle: angleAt(shoulder, elbow, wrist),
    trunkAngle: torsoFromVertical(shoulder, hip),
    kneeRiseRelative: (hip.y - knee.y) / torsoLength,
    wristRelative: (wrist.x - ankle.x) / torsoLength,
    hipRelative: (hip.x - ankle.x) / torsoLength,
    shoulderRelative: (shoulder.x - ankle.x) / torsoLength,
    visibility: mean(selected.indices.map(visibilityAt)),
    jointConfidence,
  };
}

function medianFilter(values: number[], radius = 2) {
  return values.map((_, index) => {
    const start = Math.max(0, index - radius);
    const end = Math.min(values.length, index + radius + 1);
    return median(values.slice(start, end));
  });
}

function zeroPhaseSmooth(values: number[]) {
  const filtered = medianFilter(values, 1);
  const weights = [1, 2, 3, 2, 1];
  return filtered.map((_, index) => {
    let weightedTotal = 0;
    let weightTotal = 0;
    for (let offset = -2; offset <= 2; offset += 1) {
      const sampleIndex = clamp(index + offset, 0, filtered.length - 1);
      const weight = weights[offset + 2];
      weightedTotal += filtered[sampleIndex] * weight;
      weightTotal += weight;
    }
    return weightedTotal / weightTotal;
  });
}

function estimatePeriod(values: number[], sampleRate: number) {
  const centered = values.map((value) => value - mean(values));
  const variance = centered.reduce((sum, value) => sum + value * value, 0);
  if (variance < 0.0001) return { lag: 0, correlation: 0 };
  const minLag = Math.max(3, Math.floor(sampleRate * 1));
  const maxLag = Math.min(values.length - 3, Math.ceil(sampleRate * 4));
  let bestLag = 0;
  let bestCorrelation = -1;
  for (let lag = minLag; lag <= maxLag; lag += 1) {
    let numerator = 0;
    let left = 0;
    let right = 0;
    for (let index = 0; index < centered.length - lag; index += 1) {
      numerator += centered[index] * centered[index + lag];
      left += centered[index] ** 2;
      right += centered[index + lag] ** 2;
    }
    const correlation = numerator / Math.sqrt(Math.max(0.0001, left * right));
    if (correlation > bestCorrelation) {
      bestCorrelation = correlation;
      bestLag = lag;
    }
  }
  return { lag: bestLag, correlation: clamp(bestCorrelation, 0, 1) };
}

function findCatchIndices(kneeAngles: number[], periodLag: number) {
  if (!periodLag) return [];
  const lower = percentile(kneeAngles, 0.08);
  const upper = percentile(kneeAngles, 0.92);
  const range = Math.max(8, upper - lower);
  const radius = Math.max(2, Math.floor(periodLag * 0.12));
  const threshold = lower + range * 0.34;
  const candidates: number[] = [];

  for (let index = radius; index < kneeAngles.length - radius; index += 1) {
    const window = kneeAngles.slice(index - radius, index + radius + 1);
    if (kneeAngles[index] <= threshold && kneeAngles[index] === Math.min(...window)) {
      candidates.push(index);
    }
  }

  const spaced: number[] = [];
  const minimumSpacing = Math.max(3, Math.floor(periodLag * 0.58));
  candidates.forEach((candidate) => {
    const previous = spaced.at(-1);
    if (previous === undefined || candidate - previous >= minimumSpacing) {
      spaced.push(candidate);
    } else if (kneeAngles[candidate] < kneeAngles[previous]) {
      spaced[spaced.length - 1] = candidate;
    }
  });

  if (spaced.length >= 3) return spaced;

  const anchor = kneeAngles.indexOf(Math.min(...kneeAngles));
  const scheduled = new Set(spaced);
  for (let center = anchor; center >= 0; center -= periodLag) {
    const start = Math.max(0, Math.round(center - periodLag * 0.2));
    const end = Math.min(kneeAngles.length - 1, Math.round(center + periodLag * 0.2));
    let minimumIndex = start;
    for (let index = start + 1; index <= end; index += 1) {
      if (kneeAngles[index] < kneeAngles[minimumIndex]) minimumIndex = index;
    }
    if (kneeAngles[minimumIndex] <= threshold) scheduled.add(minimumIndex);
  }
  for (let center = anchor + periodLag; center < kneeAngles.length; center += periodLag) {
    const start = Math.max(0, Math.round(center - periodLag * 0.2));
    const end = Math.min(kneeAngles.length - 1, Math.round(center + periodLag * 0.2));
    let minimumIndex = start;
    for (let index = start + 1; index <= end; index += 1) {
      if (kneeAngles[index] < kneeAngles[minimumIndex]) minimumIndex = index;
    }
    if (kneeAngles[minimumIndex] <= threshold) scheduled.add(minimumIndex);
  }
  return [...scheduled].sort((a, b) => a - b);
}

function indexOfMaximum(values: number[], start: number, end: number) {
  let result = start;
  for (let index = start + 1; index <= end; index += 1) {
    if (values[index] > values[result]) result = index;
  }
  return result;
}

function locateFinish(
  catchIndex: number,
  nextCatchIndex: number,
  legEndIndex: number,
  elbowAngles: number[],
  wristRelative: number[],
) {
  const length = nextCatchIndex - catchIndex;
  const start = Math.max(legEndIndex, catchIndex + Math.floor(length * 0.24));
  const end = Math.min(nextCatchIndex - 2, catchIndex + Math.floor(length * 0.62));
  if (end <= start) return legEndIndex;

  const wristDelta = Math.abs(wristRelative[end] - wristRelative[catchIndex]);
  const elbowRange = Math.max(...elbowAngles.slice(start, end + 1)) - Math.min(...elbowAngles.slice(start, end + 1));
  let result = start;
  let bestScore = -Infinity;
  for (let index = start; index <= end; index += 1) {
    const elbowProgress = elbowRange > 8
      ? (elbowAngles[start] - elbowAngles[index]) / elbowRange
      : 0;
    const handleProgress = wristDelta > 0.04
      ? Math.abs(wristRelative[index] - wristRelative[catchIndex]) / wristDelta
      : 0;
    const timePenalty = Math.max(0, (index - (catchIndex + length * 0.52)) / length);
    const score = elbowProgress * 0.58 + handleProgress * 0.42 - timePenalty * 0.18;
    if (score > bestScore) {
      bestScore = score;
      result = index;
    }
  }
  return Math.max(legEndIndex, result);
}

function scoreLabel(score: number | null): PhaseScore["label"] {
  if (score === null) return "Unavailable";
  if (score >= 85) return "Strong";
  if (score >= 72) return "On track";
  if (score >= 60) return "Watch";
  return "Focus";
}

function highIsGood(value: number, poor: number, good: number) {
  return clamp(((value - poor) / Math.max(0.001, good - poor)) * 100, 0, 100);
}

function lowIsGood(value: number, good: number, poor: number) {
  return clamp((1 - (value - good) / Math.max(0.001, poor - good)) * 100, 0, 100);
}

function resample(values: number[], start: number, end: number, points = 60) {
  const output: number[] = [];
  const length = Math.max(1, end - start);
  for (let point = 0; point < points; point += 1) {
    const position = start + (length * point) / (points - 1);
    const lower = Math.floor(position);
    const upper = Math.min(end, Math.ceil(position));
    const fraction = position - lower;
    output.push(values[lower] * (1 - fraction) + values[upper] * fraction);
  }
  return output;
}

function consistencyScore(
  cycles: StrokeCycle[],
  lowerBodyValues: number[],
  trunkAngles: number[],
  elbowAngles: number[],
) {
  if (cycles.length < 2) return null;
  const lowerBodyRange = Math.max(
    0.18,
    percentile(lowerBodyValues, 0.92) - percentile(lowerBodyValues, 0.08),
  );
  const trunkRange = Math.max(8, percentile(trunkAngles, 0.92) - percentile(trunkAngles, 0.08));
  const elbowRange = Math.max(15, percentile(elbowAngles, 0.92) - percentile(elbowAngles, 0.08));
  const profiles = cycles.map((cycle) => ({
    lowerBody: resample(lowerBodyValues, cycle.catchIndex, cycle.nextCatchIndex)
      .map((value) => value / lowerBodyRange),
    trunk: resample(trunkAngles, cycle.catchIndex, cycle.nextCatchIndex).map((value) => value / trunkRange),
    elbow: resample(elbowAngles, cycle.catchIndex, cycle.nextCatchIndex).map((value) => value / elbowRange),
  }));
  const template = {
    lowerBody: profiles[0].lowerBody.map((_, index) =>
      median(profiles.map((profile) => profile.lowerBody[index]))),
    trunk: profiles[0].trunk.map((_, index) => median(profiles.map((profile) => profile.trunk[index]))),
    elbow: profiles[0].elbow.map((_, index) => median(profiles.map((profile) => profile.elbow[index]))),
  };
  const deviations = profiles.map((profile) => {
    const errors = profile.lowerBody.map((_, index) =>
      ((profile.lowerBody[index] - template.lowerBody[index]) ** 2
        + (profile.trunk[index] - template.trunk[index]) ** 2
        + (profile.elbow[index] - template.elbow[index]) ** 2) / 3,
    );
    return Math.sqrt(mean(errors));
  });
  const durations = cycles.map((cycle) => cycle.nextCatchTime - cycle.catchTime);
  const timingVariability = standardDeviation(durations) / Math.max(0.1, mean(durations));
  return Math.round(clamp(100 - median(deviations) * 185 - timingVariability * 130, 35, 99));
}

function aggregateFinding(
  id: string,
  features: CycleFeatures[],
  predicate: (feature: CycleFeatures) => boolean,
  confidence: number,
  details: {
    title: string;
    description: string;
    evidence: (flagged: CycleFeatures[]) => string;
    cue: string;
    drill: string;
  },
): TechniqueFinding | null {
  const flagged = features.filter(predicate);
  const frequency = flagged.length / Math.max(1, features.length);
  if (flagged.length < 2 || frequency < 0.5 || confidence < 58) return null;
  return {
    id,
    title: details.title,
    description: details.description,
    evidence: details.evidence(flagged),
    cue: details.cue,
    drill: details.drill,
    frequency,
    confidence: Math.round(confidence),
    seekTime: flagged[0].cycle.catchTime,
    tone: frequency >= 0.72 ? "focus" : "watch",
  };
}

function buildPracticePlan(findings: TechniqueFinding[]): PracticeItem[] {
  if (!findings.length) {
    return [{
      title: "Protect the pattern as the rate changes",
      cue: "Keep the same order; shorten the recovery without scrambling it.",
      drill: "Row 10 strokes each at three controlled rates, keeping the movement sequence unchanged.",
      check: "Re-record from the same side-on angle and compare consistency at a matched rate.",
      seekTime: 0,
    }];
  }
  return [...findings]
    .sort((first, second) =>
      second.frequency * second.confidence - first.frequency * first.confidence,
    )
    .slice(0, 2)
    .map((finding) => ({
      title: finding.title.replace(/^Possible /, "Work on ").replace(/^Rushed /, "Smoother "),
      cue: finding.cue,
      drill: finding.drill,
      check: "Repeat 6–10 strokes at the same rate and camera angle, then compare the flagged timing.",
      seekTime: finding.seekTime,
    }));
}

export function analyzeSamples(
  rawSamples: RawPoseSample[],
  cameraView: CameraView,
  expectedFrameCount: number,
): AnalysisResult {
  if (rawSamples.length < 30) {
    throw new Error("Not enough reliably tracked frames were available for analysis.");
  }
  const times = rawSamples.map((sample) => sample.time);
  const sampleRate = 1 / Math.max(0.01, median(times.slice(1).map((time, index) => time - times[index])));
  const kneeAngles = zeroPhaseSmooth(rawSamples.map((sample) => sample.kneeAngle));
  const trunkAngles = zeroPhaseSmooth(rawSamples.map((sample) => sample.trunkAngle));
  const elbowAngles = zeroPhaseSmooth(rawSamples.map((sample) => sample.elbowAngle));
  const kneeRiseRelative = zeroPhaseSmooth(
    rawSamples.map((sample) => sample.kneeRiseRelative),
  );
  const wristRelative = zeroPhaseSmooth(rawSamples.map((sample) => sample.wristRelative));
  const period = estimatePeriod(kneeAngles, sampleRate);
  const catchIndices = findCatchIndices(kneeAngles, period.lag);
  const cycles: StrokeCycle[] = [];

  for (let index = 0; index < catchIndices.length - 1; index += 1) {
    const catchIndex = catchIndices[index];
    const nextCatchIndex = catchIndices[index + 1];
    const length = nextCatchIndex - catchIndex;
    if (length < sampleRate * 0.85 || length > sampleRate * 4.2) continue;
    const legSearchEnd = Math.min(nextCatchIndex - 2, catchIndex + Math.floor(length * 0.58));
    const legEndIndex = indexOfMaximum(kneeAngles, catchIndex + 1, legSearchEnd);
    const finishIndex = locateFinish(
      catchIndex,
      nextCatchIndex,
      legEndIndex,
      elbowAngles,
      wristRelative,
    );
    const driveDuration = times[finishIndex] - times[catchIndex];
    const recoveryDuration = times[nextCatchIndex] - times[finishIndex];
    if (driveDuration < 0.25 || recoveryDuration < 0.35) continue;
    const intervalVisibility = mean(
      rawSamples.slice(catchIndex, nextCatchIndex + 1).map((sample) => sample.visibility),
    );
    const rangeQuality = highIsGood(kneeAngles[legEndIndex] - kneeAngles[catchIndex], 18, 65) / 100;
    const durationQuality = driveDuration < recoveryDuration * 1.15 ? 1 : 0.72;
    cycles.push({
      id: cycles.length + 1,
      catchIndex,
      legEndIndex,
      finishIndex,
      nextCatchIndex,
      catchTime: times[catchIndex],
      legEndTime: times[legEndIndex],
      finishTime: times[finishIndex],
      nextCatchTime: times[nextCatchIndex],
      driveDuration,
      recoveryDuration,
      confidence: Math.round(clamp((intervalVisibility * 0.62 + period.correlation * 0.25 + rangeQuality * 0.13) * durationQuality * 100, 0, 98)),
      findings: [],
    });
  }

  if (cycles.length < 3) {
    throw new Error(
      "Fewer than three complete strokes could be identified reliably. Use a clip with at least 8–12 seconds of continuous rowing and begin recording before the first full stroke.",
    );
  }

  const phases: Array<{ phase: Phase; strokeId: number | null }> = rawSamples.map(() => ({
    phase: "Recovery",
    strokeId: null,
  }));
  cycles.forEach((cycle) => {
    for (let index = cycle.catchIndex; index <= cycle.nextCatchIndex; index += 1) {
      phases[index] = {
        phase: index === cycle.catchIndex
          ? "Catch"
          : index < cycle.finishIndex
            ? "Drive"
            : index === cycle.finishIndex
              ? "Finish"
              : "Recovery",
        strokeId: cycle.id,
      };
    }
  });

  const samples: PoseSample[] = rawSamples.map((sample, index) => ({
    ...sample,
    kneeAngle: kneeAngles[index],
    trunkAngle: trunkAngles[index],
    elbowAngle: elbowAngles[index],
    kneeRiseRelative: kneeRiseRelative[index],
    wristRelative: wristRelative[index],
    ...phases[index],
  }));

  const features: CycleFeatures[] = cycles.map((cycle) => {
    const kneeRange = Math.max(8, kneeAngles[cycle.legEndIndex] - kneeAngles[cycle.catchIndex]);
    const targetKnee = kneeAngles[cycle.catchIndex] + kneeRange * 0.3;
    let earlyDriveIndex = cycle.catchIndex;
    while (earlyDriveIndex < cycle.legEndIndex && kneeAngles[earlyDriveIndex] < targetKnee) {
      earlyDriveIndex += 1;
    }
    const recoveryLength = Math.max(1, cycle.nextCatchIndex - cycle.finishIndex);
    const nextCatchElbow = elbowAngles[cycle.nextCatchIndex];
    const elbowRecoveryRange = nextCatchElbow - elbowAngles[cycle.finishIndex];
    let armsReadyIndex = cycle.finishIndex;
    if (elbowRecoveryRange >= 12) {
      const elbowReadyTarget = elbowAngles[cycle.finishIndex] + elbowRecoveryRange * 0.58;
      while (
        armsReadyIndex < cycle.nextCatchIndex
        && elbowAngles[armsReadyIndex] < elbowReadyTarget
      ) {
        armsReadyIndex += 1;
      }
    }
    const trunkRecoveryRange = trunkAngles[cycle.nextCatchIndex] - trunkAngles[cycle.finishIndex];
    let bodyReadyIndex = cycle.finishIndex;
    if (Math.abs(trunkRecoveryRange) >= 6) {
      const trunkReadyTarget = trunkAngles[cycle.finishIndex] + trunkRecoveryRange * 0.52;
      while (
        bodyReadyIndex < cycle.nextCatchIndex
        && Math.abs(trunkAngles[bodyReadyIndex] - trunkAngles[cycle.finishIndex])
          < Math.abs(trunkReadyTarget - trunkAngles[cycle.finishIndex])
      ) {
        bodyReadyIndex += 1;
      }
    }
    const preparationIndex = Math.max(armsReadyIndex, bodyReadyIndex);
    const baselineEnd = Math.min(
      cycle.nextCatchIndex,
      cycle.finishIndex + Math.max(1, Math.round(sampleRate * 0.08)),
    );
    const finishKneeRise = median(
      kneeRiseRelative.slice(cycle.finishIndex, baselineEnd + 1),
    );
    const catchWindowStart = Math.max(
      cycle.finishIndex,
      cycle.nextCatchIndex - Math.max(1, Math.round(sampleRate * 0.08)),
    );
    const catchKneeRise = median(
      kneeRiseRelative.slice(catchWindowStart, cycle.nextCatchIndex + 1),
    );
    const totalKneeRise = Math.max(0, catchKneeRise - finishKneeRise);
    const preparationWindow = Math.max(1, Math.round(sampleRate * 0.12));
    const kneeRiseAtPreparation = median(
      kneeRiseRelative.slice(
        Math.max(cycle.finishIndex, preparationIndex - preparationWindow),
        Math.min(cycle.nextCatchIndex, preparationIndex + preparationWindow) + 1,
      ),
    );
    const earlyKneeBreak = totalKneeRise >= 0.18
      ? clamp((kneeRiseAtPreparation - finishKneeRise) / totalKneeRise, 0, 1)
      : 0;
    const finalStart = cycle.finishIndex + Math.floor(recoveryLength * 0.75);
    const finalCompression = Math.max(0, catchKneeRise - kneeRiseRelative[finalStart]);
    const recoveryTrackingConfidence = mean(
      rawSamples
        .slice(cycle.finishIndex, cycle.nextCatchIndex + 1)
        .map((sample) => mean([
          sample.jointConfidence.kneeRise,
          sample.jointConfidence.elbow,
          sample.jointConfidence.trunk,
        ])),
    );
    const handleTotal = Math.max(
      0.05,
      Math.abs(wristRelative[cycle.finishIndex] - wristRelative[cycle.catchIndex]),
    );
    return {
      cycle,
      catchKnee: kneeAngles[cycle.catchIndex],
      finishKnee: kneeAngles[cycle.legEndIndex],
      catchKneeRise,
      finishKneeRise,
      kneeExcursion: totalKneeRise,
      catchElbow: elbowAngles[cycle.catchIndex],
      finishElbow: elbowAngles[cycle.finishIndex],
      earlyArmFlex: Math.max(
        0,
        elbowAngles[cycle.catchIndex]
          - Math.min(...elbowAngles.slice(cycle.catchIndex, earlyDriveIndex + 1)),
      ),
      earlyTrunkChange: Math.abs(trunkAngles[earlyDriveIndex] - trunkAngles[cycle.catchIndex]),
      earlyHandleFraction: Math.abs(
        wristRelative[earlyDriveIndex] - wristRelative[cycle.catchIndex],
      ) / handleTotal,
      earlyKneeBreak,
      recoveryTrackingConfidence,
      finalCompressionFraction: finalCompression / Math.max(0.18, totalKneeRise),
      trunkSwing: Math.abs(trunkAngles[cycle.finishIndex] - trunkAngles[cycle.catchIndex]),
      driveRecovery: cycle.recoveryDuration / Math.max(0.1, cycle.driveDuration),
    };
  });

  const validFramePercent = Math.round(
    clamp((rawSamples.length / Math.max(1, expectedFrameCount)) * 100, 0, 100),
  );
  const cameraFactor: Record<CameraView, number> = {
    side: 1,
    diagonal: 0.72,
    launch: 0.55,
    bow: 0.34,
  };
  const visibility = mean(rawSamples.map((sample) => sample.visibility));
  const frameCoverage = validFramePercent / 100;
  const cycleConfidence = mean(cycles.map((cycle) => cycle.confidence)) / 100;
  const confidence = Math.round(clamp(
    (visibility * 0.34
      + period.correlation * 0.24
      + frameCoverage * 0.2
      + cycleConfidence * 0.22)
      * cameraFactor[cameraView]
      * 100,
    12,
    98,
  ));
  const confidenceLabel: ConfidenceLabel = confidence >= 82
    ? "High"
    : confidence >= 62
      ? "Medium"
      : "Low";
  const supportsTechnique = cameraView === "side" && confidence >= 60;
  const cycleDurations = cycles.map((cycle) => cycle.nextCatchTime - cycle.catchTime);
  const strokeRate = Math.round(60 / Math.max(0.1, median(cycleDurations)));
  const driveRecovery = median(features.map((feature) => feature.driveRecovery));
  const consistency = supportsTechnique
    ? consistencyScore(cycles, kneeRiseRelative, trunkAngles, elbowAngles)
    : null;

  const jointConfidence = {
    knee: mean(rawSamples.map((sample) => sample.jointConfidence.knee)) * 100,
    kneeRise: mean(rawSamples.map((sample) => sample.jointConfidence.kneeRise)) * 100,
    elbow: mean(rawSamples.map((sample) => sample.jointConfidence.elbow)) * 100,
    trunk: mean(rawSamples.map((sample) => sample.jointConfidence.trunk)) * 100,
    wrist: mean(rawSamples.map((sample) => sample.jointConfidence.wrist)) * 100,
  };
  const findingConfidence = {
    elbow: Math.min(confidence, jointConfidence.elbow),
    trunk: Math.min(confidence, jointConfidence.trunk),
    wrist: Math.min(confidence, jointConfidence.wrist),
    recovery: Math.min(confidence, jointConfidence.kneeRise, jointConfidence.elbow, jointConfidence.trunk),
  };
  const reliableRecoveryFeatures = features.filter(
    (feature) => feature.recoveryTrackingConfidence >= 0.72,
  );
  const earlyKneeCandidates = reliableRecoveryFeatures.length >= 4
    ? reliableRecoveryFeatures
    : [];
  const earlyKneeFindingCandidate = earlyKneeCandidates.length
    ? aggregateFinding(
      "early-knee-break",
      earlyKneeCandidates,
      (feature) => feature.earlyKneeBreak >= 0.3,
      Math.min(
        findingConfidence.recovery,
        mean(earlyKneeCandidates.map((feature) => feature.recoveryTrackingConfidence)) * 100,
      ),
      {
        title: "Possible early knee break",
        description: "A substantial part of knee rise appears before the hands and body establish the recovery.",
        evidence: (flagged) => `${Math.round(median(flagged.map((feature) => feature.earlyKneeBreak)) * 100)}% of measured knee rise occurred before arms-and-body preparation in ${flagged.length} of ${earlyKneeCandidates.length} reliably tracked strokes.`,
        cue: "Send the hands away and establish body-over before allowing the knees to rise.",
        drill: "Pause briefly at body-over for 10 strokes, then remove the pause while preserving the same sequence.",
      },
    )
    : null;
  const earlyKneeFinding = earlyKneeFindingCandidate
    && earlyKneeFindingCandidate.frequency >= 0.6
    ? earlyKneeFindingCandidate
    : null;

  const findings = supportsTechnique
    ? [
      aggregateFinding(
        "early-arm-bend",
        features,
        (feature) => feature.earlyArmFlex >= 12,
        findingConfidence.elbow,
        {
          title: "Possible early arm bend",
          description: "The elbow appears to flex before the first part of the leg drive is established.",
          evidence: (flagged) => `The elbow flexed about ${Math.round(median(flagged.map((feature) => feature.earlyArmFlex)))}° before 30% leg extension in ${flagged.length} of ${features.length} strokes.`,
          cue: "Keep the elbows long through the first push.",
          drill: "Row 10 legs-only strokes, 10 legs-and-body strokes, then 20 full strokes at a controlled rate.",
        },
      ),
      aggregateFinding(
        "early-body-opening",
        features,
        (feature) => feature.earlyTrunkChange >= 8,
        findingConfidence.trunk,
        {
          title: "Possible early body opening",
          description: "The trunk appears to open substantially during the beginning of the leg drive.",
          evidence: (flagged) => `Trunk angle changed about ${Math.round(median(flagged.map((feature) => feature.earlyTrunkChange)))}° before 30% leg extension in ${flagged.length} of ${features.length} strokes.`,
          cue: "Hold the catch body angle through the first push.",
          drill: "Use quarter-slide legs-only strokes, then gradually lengthen while keeping the shoulders connected over the hips.",
        },
      ),
      aggregateFinding(
        "shooting-slide",
        features,
        (feature) => feature.earlyHandleFraction < 0.18,
        findingConfidence.wrist,
        {
          title: "Possible slide shooting",
          description: "The legs appear to extend without enough matching movement through the hand path.",
          evidence: (flagged) => `Only ${Math.round(median(flagged.map((feature) => feature.earlyHandleFraction)) * 100)}% of the measured hand-path change was present by 30% leg extension in ${flagged.length} of ${features.length} strokes.`,
          cue: "Move the seat and handle together at the start.",
          drill: "Alternate five legs-only strokes with five full strokes, matching the initial handle movement in both.",
        },
      ),
      earlyKneeFinding,
      aggregateFinding(
        "rushed-slide",
        features,
        (feature) => feature.finalCompressionFraction > 0.44,
        Math.min(confidence, jointConfidence.knee),
        {
          title: "Rushed approach to the catch",
          description: "A large share of the slide compression occurs near the end of the recovery.",
          evidence: (flagged) => `${Math.round(median(flagged.map((feature) => feature.finalCompressionFraction)) * 100)}% of measured compression occurred in the final quarter of recovery in ${flagged.length} of ${features.length} strokes.`,
          cue: "Stay patient into the front end.",
          drill: "Use a light body-over pause, then glide through the final quarter of the slide without accelerating into the catch.",
        },
      ),
    ].filter((finding): finding is TechniqueFinding => Boolean(finding))
    : [];

  cycles.forEach((cycle) => {
    cycle.findings = findings
      .filter((finding) => Math.abs(finding.seekTime - cycle.catchTime) < 0.04)
      .map((finding) => finding.id);
  });

  const catchConsistency = lowIsGood(
    standardDeviation(features.map((feature) => feature.catchKneeRise)),
    0.035,
    0.14,
  );
  const catchReadiness = highIsGood(median(features.map((feature) => feature.catchElbow)), 140, 168);
  const catchControl = lowIsGood(
    median(features.map((feature) => feature.finalCompressionFraction)),
    0.3,
    0.62,
  );
  const driveArm = lowIsGood(median(features.map((feature) => feature.earlyArmFlex)), 4, 20);
  const driveTrunk = lowIsGood(median(features.map((feature) => feature.earlyTrunkChange)), 3, 14);
  const driveConnection = highIsGood(median(features.map((feature) => feature.earlyHandleFraction)), 0.08, 0.32);
  const finishExtension = highIsGood(
    median(features.map((feature) => feature.kneeExcursion)),
    0.16,
    0.38,
  );
  const finishConsistency = lowIsGood(
    standardDeviation(features.map((feature) => feature.finishKneeRise)),
    0.035,
    0.14,
  );
  const finishPostureConsistency =
    lowIsGood(standardDeviation(features.map((feature) => feature.finishElbow)), 4, 16) * 0.55
    + lowIsGood(standardDeviation(features.map((feature) => feature.trunkSwing)), 2.5, 10) * 0.45;
  const hasReliableRecoveryOrder = reliableRecoveryFeatures.length >= 3;
  const recoveryOrder = hasReliableRecoveryOrder
    ? lowIsGood(
      median(reliableRecoveryFeatures.map((feature) => feature.earlyKneeBreak)),
      0.1,
      0.38,
    )
    : 72;
  const recoveryControl = lowIsGood(
    median(features.map((feature) => feature.finalCompressionFraction)),
    0.3,
    0.62,
  );
  const expectedRecoveryRatio = clamp(2.75 - strokeRate * 0.04, 1.25, 2.15);
  const rhythmScore = lowIsGood(Math.abs(driveRecovery - expectedRecoveryRatio), 0.15, 0.9);
  const rawPhaseValues: Record<Phase, number> = {
    Catch: catchConsistency * 0.34 + catchReadiness * 0.36 + catchControl * 0.3,
    Drive: driveArm * 0.425 + driveTrunk * 0.425 + driveConnection * 0.15,
    Finish: finishExtension * 0.32 + finishConsistency * 0.23 + finishPostureConsistency * 0.45,
    Recovery: recoveryOrder * 0.4 + recoveryControl * 0.3 + rhythmScore * 0.3,
  };
  const summaries: Record<Phase, string> = {
    Catch: "Preparation, compression repeatability, and control into the front end.",
    Drive: "Leg–trunk–arm timing and early handle connection.",
    Finish: "Leg-drive completion and repeatability at the back end.",
    Recovery: "Hands–body–slide order, rhythm, and approach control.",
  };
  const phaseScores: PhaseScore[] = (["Catch", "Drive", "Finish", "Recovery"] as Phase[])
    .map((phase) => {
      const score = supportsTechnique ? Math.round(clamp(rawPhaseValues[phase], 35, 98)) : null;
      return {
        phase,
        score,
        confidence,
        label: scoreLabel(score),
        summary: supportsTechnique
          ? summaries[phase]
          : "A fixed side-on recording is required for this phase score.",
      };
    });
  const scoreByPhase = Object.fromEntries(
    phaseScores.map((phaseScore) => [phaseScore.phase, phaseScore.score ?? 0]),
  ) as Record<Phase, number>;
  const overallScore = supportsTechnique
    ? Math.round(
      scoreByPhase.Catch * 0.22
      + scoreByPhase.Drive * 0.36
      + scoreByPhase.Finish * 0.17
      + scoreByPhase.Recovery * 0.25,
    )
    : null;
  const sequence = supportsTechnique ? scoreByPhase.Drive : null;
  const limitations = [
    "Catch and finish are body-motion estimates; blade entry and extraction are not confirmed.",
    "Angles are two-dimensional camera-plane estimates, not laboratory 3D measurements.",
  ];
  if (cameraView !== "side") {
    limitations.unshift(
      "This recording view supports rhythm only. Phase scoring and technique findings require a fixed side-on view.",
    );
  }
  if (confidence < 60) {
    limitations.unshift(
      "Tracking confidence was too low for technique scoring. Use a brighter, steadier clip with the full athlete visible.",
    );
  }
  if (supportsTechnique && !hasReliableRecoveryOrder) {
    limitations.unshift(
      "Knee-break timing had limited scoring weight because fewer than three recovery phases had reliable hip, knee, elbow, and trunk tracking.",
    );
  }

  return {
    samples,
    cycles,
    phaseScores,
    findings,
    practicePlan: buildPracticePlan(findings),
    limitations,
    metrics: {
      strokeRate: period.correlation >= 0.22 ? strokeRate : null,
      driveRecovery: period.correlation >= 0.22 ? driveRecovery : null,
      sequence,
      consistency,
      confidence,
      confidenceLabel,
      catchKnee: supportsTechnique ? Math.round(median(features.map((feature) => feature.catchKnee))) : null,
      finishKnee: supportsTechnique ? Math.round(median(features.map((feature) => feature.finishKnee))) : null,
      trunkSwing: supportsTechnique ? Math.round(median(features.map((feature) => feature.trunkSwing))) : null,
      elbowRange: supportsTechnique
        ? Math.round(percentile(elbowAngles, 0.92) - percentile(elbowAngles, 0.08))
        : null,
      analyzedSeconds: Math.max(0, times.at(-1)! - times[0]),
      strokesObserved: cycles.length,
      periodicity: period.correlation,
      overallScore,
      validFramePercent,
    },
  };
}
